import { useEffect, useRef, useState } from 'react';
import { getFilterEmptyLabel, getFilterLabel } from '@/shared/copy/filters';
import { r, s } from '@/theme/tokens';
import { Autocomplete, Box, Checkbox, CloseIcon, Text, TextField } from '@/ui/primitives';
import {
  buildFilterAutocompleteListboxSx,
  filterInputSx,
  mergeFilterSlotSx,
} from './filterControlStyles';
import type { FormFieldBackgroundColor } from '@/ui/primitives/formFieldVariants';
import type { ReactElement } from 'react';

type TagOption = { value: string; label: string; count?: number };

const filterPopperDataAttribute = { 'data-flowqa-filter-popper': 'true' } as const;

type TagsFilterFieldProps = {
  value: string[];
  options: TagOption[];
  label?: string;
  showLabel?: boolean;
  fontSizePx?: number;
  labelPlacement?: 'inline' | 'stacked';
  labelControlGap?: string;
  inputAriaLabel?: string;
  emptyLabel?: string;
  fullWidth?: boolean;
  controlWidthPx?: number;
  inputBackgroundColor?: FormFieldBackgroundColor;
  dropdownBackgroundColor?: FormFieldBackgroundColor;
  inputLeftPadding?: string;
  controlRadius?: string;
  onChange: (next: string[]) => void;
};

export const TagsFilterField = ({
  value,
  options,
  label = getFilterLabel('tags'),
  showLabel = true,
  fontSizePx = 15,
  labelPlacement = 'inline',
  labelControlGap,
  emptyLabel = getFilterEmptyLabel('allTags'),
  inputAriaLabel,
  fullWidth = false,
  controlWidthPx,
  inputBackgroundColor = 'var(--surface-page)',
  dropdownBackgroundColor = 'var(--surface-page)',
  inputLeftPadding = s('sm'),
  controlRadius = r('md'),
  onChange,
}: TagsFilterFieldProps): ReactElement => {
  const controlRef = useRef<HTMLDivElement | null>(null);
  const emptyOptionValue = '__flowqa_no_options__';
  const effectiveOptions =
    options.length > 0 ? options : [{ value: emptyOptionValue, label: 'No options' }];
  const selectedOptions = options.filter((option) => value.includes(option.value));
  const hasValue = selectedOptions.length > 0;
  const inputLeftInset = selectedOptions.length === 0 ? inputLeftPadding : s('none');
  const fixedFilterWidth = `calc(${s('5xl')} + ${s('2xl')})`;
  const resolvedControlWidth = controlWidthPx ? `${controlWidthPx}px` : fixedFilterWidth;
  const optionRowMinHeight = `calc(${s('2xl')} + ${s('xl')} + ${s('sm')})`;
  const optionHorizontalPadding = s('md');
  const optionCheckboxSlotWidth = `calc(${s('lg')} + ${s('sm')})`;
  const controlHeight = 32;
  const controlWidthValuePx = controlWidthPx ?? 180;
  const endAdornmentPadding =
    selectedOptions.length > 0 ? s('2xl') : `calc(${s('2xl')} + ${s('xxs')})`;
  const isInlineLabel = labelPlacement === 'inline';
  const shouldShowLabel = showLabel && label.length > 0;
  const [popperPlacement, setPopperPlacement] = useState<'bottom-start' | 'bottom-end'>(
    'bottom-start',
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const estimatedDropdownWidth = Math.min(
    Math.max(
      controlWidthValuePx,
      options.reduce((maxWidth, option) => {
        const countWidth =
          option.count === undefined ? 0 : option.count.toLocaleString().length * 8 + 32;
        return Math.max(maxWidth, option.label.length * 13 + countWidth + 88);
      }, 0),
    ),
    640,
  );

  useEffect(() => {
    const updatePlacement = (): void => {
      if (typeof window === 'undefined') {
        return;
      }
      const rect = controlRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      const viewportPadding = 16;
      const rightSpace = window.innerWidth - rect.left - viewportPadding;
      const leftSpace = rect.right - viewportPadding;
      setPopperPlacement(
        rightSpace < estimatedDropdownWidth && leftSpace > rightSpace
          ? 'bottom-end'
          : 'bottom-start',
      );
    };

    updatePlacement();
    window.addEventListener('resize', updatePlacement);
    return () => {
      window.removeEventListener('resize', updatePlacement);
    };
  }, [estimatedDropdownWidth]);

  useEffect(() => {
    if (!isDropdownOpen) {
      return;
    }

    const closeOnOutsidePointerDown = (event: MouseEvent | TouchEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (controlRef.current?.contains(target)) {
        return;
      }
      if (target instanceof Element && target.closest('[data-flowqa-filter-popper="true"]')) {
        return;
      }
      setIsDropdownOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsidePointerDown);
    document.addEventListener('touchstart', closeOnOutsidePointerDown);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsidePointerDown);
      document.removeEventListener('touchstart', closeOnOutsidePointerDown);
    };
  }, [isDropdownOpen]);

  const heightSx = {
    '& .MuiAutocomplete-inputRoot': {
      minHeight: `${controlHeight}px !important`,
      height: `${controlHeight}px !important`,
      maxHeight: `${controlHeight}px !important`,
      backgroundColor: inputBackgroundColor,
      alignItems: 'center',
      flexWrap: 'nowrap !important',
      overflow: 'hidden',
      paddingTop: `${s('none')} !important`,
      paddingBottom: `${s('none')} !important`,
      paddingRight: `${endAdornmentPadding} !important`,
      minWidth: 0,
      cursor: 'pointer',
      '& .MuiAutocomplete-endAdornment': {
        right: s('none'),
        display: 'flex',
        alignItems: 'center',
        gap: s('none'),
        '& > button + button': {
          marginLeft: `calc(${s('xxs')} - ${s('md')}) !important`,
        },
      },
      '& .MuiAutocomplete-clearIndicator': {
        margin: `${s('none')} !important`,
        marginRight: `${s('none')} !important`,
        padding: `${s('xxs')} !important`,
        width: 'auto',
        height: 'auto',
        opacity: 1,
        visibility: 'visible',
        pointerEvents: 'auto',
      },
      '& .MuiAutocomplete-popupIndicator': {
        padding: `${s('xxs')} !important`,
        margin: `${s('none')} !important`,
        marginRight: `${s('none')} !important`,
        width: 'auto',
        height: 'auto',
      },
    },
    '& .MuiAutocomplete-input': {
      minHeight: `${controlHeight}px !important`,
      height: `${controlHeight}px !important`,
      minWidth: hasValue ? '2ch !important' : undefined,
      width: hasValue ? '2ch !important' : 'auto !important',
      flex: hasValue ? '0 0 2ch' : '1 1 auto',
      paddingTop: `${s('none')} !important`,
      paddingBottom: `${s('none')} !important`,
      paddingLeft: `${inputLeftInset} !important`,
      marginLeft: `${s('none')} !important`,
      lineHeight: '1 !important',
      cursor: 'pointer',
    },
  } as const;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isInlineLabel ? 'row' : 'column',
        alignItems: isInlineLabel ? 'center' : 'stretch',
        gap: shouldShowLabel ? (isInlineLabel ? (labelControlGap ?? s('sm')) : s('xs')) : s('none'),
        minWidth: 0,
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      {shouldShowLabel ? (
        <Text
          role="label"
          color="textSecondary"
          transform="none"
          sx={{
            whiteSpace: isInlineLabel ? 'nowrap' : 'normal',
            fontSize: `${fontSizePx}px`,
            lineHeight: 1.4,
            fontWeight: (theme) => theme.typography.fontWeightRegular,
          }}
        >
          {label}
        </Text>
      ) : null}
      <Box
        ref={controlRef}
        sx={{
          width: fullWidth ? '100%' : resolvedControlWidth,
          minWidth: fullWidth ? 0 : resolvedControlWidth,
          maxWidth: fullWidth ? 'none' : resolvedControlWidth,
          flex: fullWidth ? '1 1 0' : `0 0 ${resolvedControlWidth}`,
        }}
      >
        <Autocomplete
          multiple
          disableCloseOnSelect
          disablePortal={false}
          fullWidth={false}
          open={isDropdownOpen}
          onOpen={() => {
            setIsDropdownOpen(true);
          }}
          onClose={(_event, reason) => {
            if (reason === 'selectOption' || reason === 'removeOption' || reason === 'blur') {
              return;
            }
            setIsDropdownOpen(false);
          }}
          clearIcon={<CloseIcon fontSize="small" />}
          options={effectiveOptions}
          value={selectedOptions}
          isOptionEqualToValue={(option, selectedOption) => option.value === selectedOption.value}
          getOptionLabel={(option) => option.label}
          getOptionDisabled={(option) => option.value === emptyOptionValue}
          onChange={(_event, nextOptions) => {
            setIsDropdownOpen(true);
            onChange(
              nextOptions
                .map((option) => option.value)
                .filter((optionValue) => optionValue !== emptyOptionValue),
            );
          }}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            const isEmptyOption = option.value === emptyOptionValue;
            return (
              <Box
                component="li"
                key={key}
                {...optionProps}
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: s('sm'),
                  px: s('none'),
                  py: s('md'),
                  minHeight: optionRowMinHeight,
                  minWidth: 0,
                }}
              >
                {isEmptyOption ? (
                  <Box
                    aria-hidden="true"
                    sx={{ width: optionCheckboxSlotWidth, minWidth: optionCheckboxSlotWidth }}
                  />
                ) : (
                  <Checkbox
                    size="small"
                    checked={selected}
                    sx={{
                      p: s('none'),
                      m: s('none'),
                      mr: s('xs'),
                      '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                    }}
                  />
                )}
                <Text
                  role="bodyMuted"
                  color="textPrimary"
                  component="span"
                  sx={{
                    fontSize: `${fontSizePx}px`,
                    lineHeight: 1.4,
                    fontWeight: (theme) => theme.typography.fontWeightLight,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {option.label}
                </Text>
                {option.count !== undefined && !isEmptyOption ? (
                  <Text
                    role="bodyMuted"
                    color="textSecondary"
                    component="span"
                    sx={{
                      ml: 'auto',
                      pl: s('lg'),
                      flex: '0 0 auto',
                      fontSize: `${fontSizePx}px`,
                      lineHeight: 1.4,
                      fontWeight: (theme) => theme.typography.fontWeightRegular,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {option.count.toLocaleString()}
                  </Text>
                ) : null}
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              fieldStyle="filter"
              inputHeight={controlHeight}
              inputBackgroundColor={inputBackgroundColor}
              placeholder={!hasValue ? emptyLabel : undefined}
              // eslint-disable-next-line @typescript-eslint/no-deprecated -- Autocomplete renderInput params supply inputProps with required refs/handlers.
              inputProps={{
                ...params.inputProps,
                'aria-label': inputAriaLabel ?? label,
                readOnly: true,
                style: { cursor: 'pointer' },
              }}
            />
          )}
          sx={(theme) => ({
            ...(typeof filterInputSx === 'function' ? filterInputSx(theme) : {}),
            ...heightSx,
            width: '100%',
            minWidth: 0,
            '& .MuiInputBase-root, & .MuiOutlinedInput-root, & .MuiAutocomplete-inputRoot': {
              ...theme.typography.body1,
              fontSize: `${fontSizePx}px`,
              borderRadius: `${controlRadius} !important`,
              cursor: 'pointer',
              color: theme.palette.common.white,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderRadius: `${controlRadius} !important`,
            },
            '& .MuiInputBase-input, & .MuiAutocomplete-input, & input': {
              ...theme.typography.body1,
              fontSize: `${fontSizePx}px`,
              cursor: 'pointer',
              color: theme.palette.common.white,
            },
            '& .MuiInputBase-input::placeholder, & .MuiAutocomplete-input::placeholder, & input::placeholder':
              {
                ...theme.typography.body1,
                fontSize: `${fontSizePx}px`,
                color: theme.palette.common.white,
                opacity: 1,
              },
          })}
          slotProps={{
            popper: {
              ...filterPopperDataAttribute,
              placement: popperPlacement,
              popperOptions: {
                modifiers: [
                  {
                    name: 'flip',
                    enabled: false,
                  },
                  {
                    name: 'preventOverflow',
                    options: {
                      padding: 16,
                      mainAxis: false,
                      altAxis: true,
                    },
                  },
                ],
              },
              modifiers: [
                {
                  name: 'flip',
                  enabled: false,
                },
                {
                  name: 'preventOverflow',
                  options: {
                    padding: 16,
                    mainAxis: false,
                    altAxis: true,
                  },
                },
              ],
              sx: {
                width: 'max-content !important',
                minWidth: `${controlWidthValuePx}px !important`,
                maxWidth: 'min(640px, calc(100vw - 32px)) !important',
                '& .MuiAutocomplete-paper': {
                  minWidth: '100%',
                  width: '100%',
                  maxWidth: '100%',
                },
              },
            },
            paper: {
              sx: {
                backgroundColor: dropdownBackgroundColor,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: `${controlRadius} !important`,
                overflow: 'hidden',
                boxShadow: 'none',
                backgroundImage: 'none',
                '--Paper-overlay': 'none',
                '--Paper-shadow': 'none',
                minWidth: '100%',
                width: '100%',
                maxWidth: '100%',
              },
            },
            clearIndicator: {
              sx: {
                margin: `${s('none')} !important`,
                marginRight: `${s('none')} !important`,
                padding: `${s('xxs')} !important`,
                width: 'auto',
                height: 'auto',
                opacity: 1,
                visibility: 'visible',
                pointerEvents: 'auto',
                color: 'common.white',
              },
            },
            listbox: {
              sx: mergeFilterSlotSx(buildFilterAutocompleteListboxSx(optionRowMinHeight), {
                py: s('none'),
                minWidth: resolvedControlWidth,
                '& .MuiAutocomplete-option': {
                  paddingLeft: `${optionHorizontalPadding} !important`,
                  paddingRight: `${optionHorizontalPadding} !important`,
                  whiteSpace: 'nowrap',
                },
              }),
            },
          }}
          renderValue={(selected) => {
            const selectedValues = Array.isArray(selected) ? selected : [];
            if (selectedValues.length === 0) return null;
            return (
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  minWidth: 0,
                  maxWidth: '100%',
                  pl: s('xs'),
                  pr: s('none'),
                  color: 'text.primary',
                  font: 'inherit',
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                  lineHeight: `${controlHeight}px`,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {selectedValues.map((option) => option.label).join(', ')}
              </Box>
            );
          }}
        />
      </Box>
    </Box>
  );
};

export type { TagOption, TagsFilterFieldProps };
